/**
 * NAD Menu Bar Component
 * Handles menu interactions and mobile menu functionality
 */
declare global {
  interface Window {
    nadMenuBar?: NADMenuBar;
  }
}

type Nullable<T> = T | null;

class NADMenuBar {
  private menuBar: Nullable<HTMLElement>;
  private mobileToggle: Nullable<HTMLElement>;
  private mobileModal: Nullable<HTMLElement>;
  private mobileClose: Nullable<HTMLElement>;
  private mobileOverlay: Nullable<HTMLElement>;
  private isMenuOpen: boolean;
  private cartCountElement: Nullable<HTMLElement>;
  private isFetchingCart: boolean;
  private hoverHandlers: Array<{ element: HTMLElement; mouseenterHandler: () => void; mouseleaveHandler: () => void }> = [];
  
  // ADA Compliance - Focus Management
  private focusableElements: HTMLElement[] = [];
  private firstFocusableElement: Nullable<HTMLElement> = null;
  private lastFocusableElement: Nullable<HTMLElement> = null;
  private previouslyFocusedElement: Nullable<HTMLElement> = null;

  constructor() {
    this.menuBar = document.querySelector(".nad-menu-bar");
    this.mobileToggle = document.querySelector(".nad-menu-bar__mobile-toggle");
    this.mobileModal = document.querySelector(".nad-menu-bar__mobile-modal");
    this.mobileClose = document.querySelector(".nad-menu-bar__mobile-close");
    this.mobileOverlay = document.querySelector(
      ".nad-menu-bar__mobile-overlay"
    );
    this.cartCountElement = document.querySelector(".nad-menu-bar__cart-count");
    this.isMenuOpen = false;
    this.isFetchingCart = false;

    if (this.menuBar) {
      this.init();
    }
  }

  private init(): void {
    console.log("NAD Menu Bar initialized");
    this.bindEvents();
    this.initCartCounter();
    this.bindMobileMenu();
    this.initFocusManagement();
    this.initHeaderHeightVar();
  }

  /**
   * Expose the sticky header's height as --nad-header-height so page CSS can
   * offset content under it (e.g. the home hero slides under the menu bar)
   */
  private initHeaderHeightVar(): void {
    const headerSection = this.menuBar?.closest<HTMLElement>(
      ".section-nad-menu-bar"
    );
    if (!headerSection) return;

    const setHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--nad-header-height",
        `${headerSection.offsetHeight}px`
      );
    };

    setHeaderHeight();
    window.addEventListener("resize", setHeaderHeight);
  }

  /**
   * Initialize focus management for ADA compliance
   */
  private initFocusManagement(): void {
    // Store reference to previously focused element
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    
    // Initialize focusable elements for mobile modal
    this.updateFocusableElements();
  }

  /**
   * Update list of focusable elements in mobile modal
   */
  private updateFocusableElements(): void {
    if (!this.mobileModal) return;
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    this.focusableElements = Array.from(
      this.mobileModal.querySelectorAll<HTMLElement>(focusableSelectors)
    );
    
    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null;
  }

  /**
   * Handle focus trap for mobile modal
   */
  private handleFocusTrap(e: KeyboardEvent): void {
    if (!this.isMenuOpen || !this.mobileModal) return;
    
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement?.focus();
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement?.focus();
        }
      }
    }
  } 

  private bindEvents(): void {
    // Handle menu item interactions
    this.bindMenuItems();
  }

  private bindMenuItems(): void {
    if (!this.menuBar) return;
    
    // Handle dropdown toggles
    const dropdownToggles = this.menuBar.querySelectorAll<HTMLButtonElement>("[data-menu-toggle]");
    const dropdownItems = this.menuBar.querySelectorAll<HTMLElement>(".nad-menu-item--has-dropdown");
    
    // Add hover functionality to dropdown items
    dropdownItems.forEach((item) => {
      const mouseenterHandler = () => {
        item.classList.add("nad-menu-item--hover");
      };
      
      const mouseleaveHandler = () => {
        item.classList.remove("nad-menu-item--hover");
      };
      
      item.addEventListener("mouseenter", mouseenterHandler);
      item.addEventListener("mouseleave", mouseleaveHandler);
      
      // Store handlers for cleanup
      this.hoverHandlers.push({
        element: item,
        mouseenterHandler,
        mouseleaveHandler
      });
    });
    
    dropdownToggles.forEach((toggle, index) => {
      const parentItem = toggle.closest(".nad-menu-item--has-dropdown");
      const dropdown = parentItem?.querySelector("[data-menu-dropdown]");
      
      if (!parentItem || !dropdown) return;
      
      // Handle keyboard navigation
      toggle.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.toggleDropdown(parentItem as HTMLElement, toggle);
        } else if (e.key === "Escape") {
          this.closeAllDropdowns();
          toggle.focus();
        }
      });
      
      // Handle click for mobile/touch devices
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDropdown(parentItem as HTMLElement, toggle);
      });
      
      // Close dropdown when clicking outside
      document.addEventListener("click", (e) => {
        if (!parentItem.contains(e.target as Node)) {
          this.closeDropdown(parentItem as HTMLElement, toggle);
        }
      });
      
      // Handle dropdown item keyboard navigation
      const dropdownLinks = dropdown.querySelectorAll<HTMLAnchorElement>("a");
      dropdownLinks.forEach((link, linkIndex) => {
        link.addEventListener("keydown", (e) => {
          if (e.key === "Escape") {
            this.closeDropdown(parentItem as HTMLElement, toggle);
            toggle.focus();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const nextLink = dropdownLinks[linkIndex + 1];
            if (nextLink) {
              nextLink.focus();
            } else {
              // Wrap to first link
              dropdownLinks[0]?.focus();
            }
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (linkIndex === 0) {
              toggle.focus();
            } else {
              const prevLink = dropdownLinks[linkIndex - 1];
              if (prevLink) prevLink.focus();
            }
          } else if (e.key === "Home") {
            e.preventDefault();
            dropdownLinks[0]?.focus();
          } else if (e.key === "End") {
            e.preventDefault();
            dropdownLinks[dropdownLinks.length - 1]?.focus();
          }
        });
      });
    });
    
    // Handle single menu items
    const singleMenuItems = this.menuBar.querySelectorAll<HTMLElement>(".nad-menu-item--single");
    singleMenuItems.forEach((item) => {
      const link = item.querySelector<HTMLAnchorElement>(".nad-menu-item__link");
      if (link) {
        link.addEventListener("click", () => {
          console.log("Menu item clicked:", link.textContent);
        });
      }
    });
  }
  
  private toggleDropdown(parentItem: HTMLElement, toggle: HTMLButtonElement): void {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    
    // Close all other dropdowns first
    this.closeAllDropdowns();
    
    if (!isOpen) {
      this.openDropdown(parentItem, toggle);
    }
  }
  
  private openDropdown(parentItem: HTMLElement, toggle: HTMLButtonElement): void {
    toggle.setAttribute("aria-expanded", "true");
    parentItem.classList.add("nad-menu-item--dropdown-open");
    
    // Focus first dropdown item after a short delay
    setTimeout(() => {
      const firstLink = parentItem.querySelector<HTMLAnchorElement>("[data-menu-dropdown] a");
      if (firstLink) firstLink.focus();
    }, 100);
  }
  
  private closeDropdown(parentItem: HTMLElement, toggle: HTMLButtonElement): void {
    toggle.setAttribute("aria-expanded", "false");
    parentItem.classList.remove("nad-menu-item--dropdown-open");
  }
  
  private closeAllDropdowns(): void {
    if (!this.menuBar) return;
    
    const openDropdowns = this.menuBar.querySelectorAll<HTMLElement>(".nad-menu-item--dropdown-open");
    openDropdowns.forEach((item) => {
      const toggle = item.querySelector<HTMLButtonElement>("[data-menu-toggle]");
      if (toggle) {
        this.closeDropdown(item, toggle);
      }
    });
  }

  /**
   * Initialize cart counter functionality for UpCart
   */
  private initCartCounter(): void {
    // Initial cart count update
    this.fetchCart();

    // Listen for UpCart specific events
    window.addEventListener('aftersell-upcart-debug', (event) => {
      if (!this.isFetchingCart) {
        this.isFetchingCart = true;
        this.fetchCart();
      }
    });

    window.addEventListener('abra:aftersell-upcart:tiers:activate', (event) => {
      this.fetchCart();
    });
  }

  /**
   * Update cart counter with cart data
   */
  private updateCartCount(cart: any): void {
    // console.log('Updating cart count:', cart.item_count, this.cartCountElement);
    if (this.cartCountElement) {
      this.cartCountElement.textContent = cart.item_count.toString();
    }
  }

  /**
   * Fetch cart data from Shopify
   */
  private fetchCart(): void {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        this.updateCartCount(cart);
      })
      .catch((error) => console.error('Error fetching cart:', error))
      .finally(() => {
        this.isFetchingCart = false;
      });
  }


  // Dropdown methods removed - now handled by CSS-only hover effects
  // The showDropdown and hideDropdown methods are no longer needed
  // as dropdown animations are now handled purely with CSS 

  private bindMobileMenu(): void {
    if (!this.mobileToggle || !this.mobileModal) return;

    // Toggle mobile menu
    this.mobileToggle.addEventListener("click", (e: Event) => {
      e.preventDefault?.();
      this.openMobileMenu();
    });

    // Close menu with close button
    if (this.mobileClose) {
      this.mobileClose.addEventListener("click", (e: Event) => {
        e.preventDefault?.();
        this.closeMobileMenu();
      });
    }

    // Close menu when clicking overlay
    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener("click", () => {
        this.closeMobileMenu();
      });
    }

    // Close menu on escape key and handle focus trap
    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.isMenuOpen) {
        this.closeMobileMenu();
      } else {
        this.handleFocusTrap(e);
      }
    });

    // Initialize mobile accordions
    this.initMobileAccordions();

    // Handle mobile menu product clicks
    const mobileProducts = this.mobileModal.querySelectorAll<HTMLAnchorElement>(
      ".nad-menu-bar__mobile-product"
    );
    mobileProducts.forEach((product) => {
      product.addEventListener("click", () => {
        const titleEl = product.querySelector(
          ".nad-menu-bar__mobile-product-title"
        );
        const title = titleEl && (titleEl.textContent?.trim() || "");
        this.closeMobileMenu();
      });
    });

    // Handle shop all click
    const shopAllLink = this.mobileModal.querySelector<HTMLAnchorElement>(
      ".nad-menu-bar__mobile-shop-all-link"
    );
    if (shopAllLink) {
      shopAllLink.addEventListener("click", () => {
        this.closeMobileMenu();
      });
    }
  }

  public toggleMobileMenu(): void {
    if (this.isMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  public openMobileMenu(): void {
    if (!this.mobileToggle || !this.mobileModal) return;
    
    // Store currently focused element before opening modal
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    
    this.isMenuOpen = true;
    this.mobileToggle.setAttribute("aria-expanded", "true");
    this.mobileModal.setAttribute("aria-hidden", "false");
    this.mobileModal.classList.add("nad-menu-bar__mobile-modal--open");

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Update focusable elements and focus management
    this.updateFocusableElements();
    
    // Focus first focusable element (close button)
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }

    // Clean up will-change after animations complete
    setTimeout(() => {
      this.cleanUpWillChange();
    }, 600); // Wait for all staggered animations to complete
  }

  public closeMobileMenu(): void {
    if (!this.mobileToggle || !this.mobileModal) return;
    this.isMenuOpen = false;
    this.mobileToggle.setAttribute("aria-expanded", "false");
    this.mobileModal.setAttribute("aria-hidden", "true");
    this.mobileModal.classList.remove("nad-menu-bar__mobile-modal--open");

    // Restore body scroll
    document.body.style.overflow = "";

    // Return focus to previously focused element or toggle button
    if (this.previouslyFocusedElement && this.previouslyFocusedElement !== this.mobileToggle) {
      this.previouslyFocusedElement.focus();
    } else {
      (this.mobileToggle as HTMLElement).focus();
    }
    
    // Clear reference
    this.previouslyFocusedElement = null;
  }

  private cleanUpWillChange(): void {
    if (!this.mobileModal) return;
    
    // Remove will-change from animated elements to improve performance
    const animatedElements = this.mobileModal.querySelectorAll(
      '.nad-menu-bar__mobile-content, .nad-menu-bar__mobile-category, .nad-menu-bar__mobile-category-title, .nad-menu-bar__mobile-product'
    );
    
    animatedElements.forEach((element) => {
      (element as HTMLElement).style.willChange = 'auto';
    });
  }

  /**
   * Initialize mobile accordion functionality
   */
  private initMobileAccordions(): void {
    if (!this.mobileModal) return;

    const accordionToggles = this.mobileModal.querySelectorAll<HTMLButtonElement>(
      "[data-accordion-toggle]"
    );

    accordionToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e: Event) => {
        e.preventDefault();
        this.toggleMobileAccordion(toggle);
      });

      // Handle keyboard navigation
      toggle.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.toggleMobileAccordion(toggle);
        } else if (e.key === "Escape") {
          this.closeAllMobileAccordions();
          toggle.focus();
        } else if (e.key === "ArrowDown" && toggle.getAttribute("aria-expanded") === "true") {
          e.preventDefault();
          const contentId = toggle.getAttribute("aria-controls");
          if (contentId) {
            const content = this.mobileModal?.querySelector<HTMLElement>(`#${contentId}`);
            const firstFocusable = content?.querySelector<HTMLElement>(
              "a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])"
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }
      });
    });
  }

  /**
   * Toggle mobile accordion open/closed state
   */
  private toggleMobileAccordion(toggle: HTMLButtonElement): void {
    const isExpanded = toggle.getAttribute("aria-expanded") === "true";
    const contentId = toggle.getAttribute("aria-controls");
    
    if (!contentId) return;

    const content = this.mobileModal?.querySelector<HTMLElement>(`#${contentId}`);
    if (!content) return;

    if (isExpanded) {
      this.closeMobileAccordion(toggle, content);
    } else {
      // Close all other accordions first (optional - remove if you want multiple open)
      this.closeAllMobileAccordions();
      this.openMobileAccordion(toggle, content);
    }
  }

  /**
   * Open mobile accordion
   */
  private openMobileAccordion(toggle: HTMLButtonElement, content: HTMLElement): void {
    toggle.setAttribute("aria-expanded", "true");
    content.classList.add("nad-menu-bar__mobile-accordion-content--open");
    
    // Update focusable elements after accordion opens
    this.updateFocusableElements();
    
    // Focus first focusable element in content after animation
    setTimeout(() => {
      const firstFocusable = content.querySelector<HTMLElement>(
        "a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 300);
  }

  /**
   * Close mobile accordion
   */
  private closeMobileAccordion(toggle: HTMLButtonElement, content: HTMLElement): void {
    toggle.setAttribute("aria-expanded", "false");
    content.classList.remove("nad-menu-bar__mobile-accordion-content--open");
    
    // Update focusable elements after accordion closes
    this.updateFocusableElements();
  }

  /**
   * Close all mobile accordions
   */
  private closeAllMobileAccordions(): void {
    if (!this.mobileModal) return;

    const openToggles = this.mobileModal.querySelectorAll<HTMLButtonElement>(
      "[data-accordion-toggle][aria-expanded='true']"
    );

    openToggles.forEach((toggle) => {
      const contentId = toggle.getAttribute("aria-controls");
      if (contentId) {
        const content = this.mobileModal?.querySelector<HTMLElement>(`#${contentId}`);
        if (content) {
          this.closeMobileAccordion(toggle, content);
        }
      }
    });
  }


  public destroy(): void {
    // Clean up hover event listeners
    this.hoverHandlers.forEach(({ element, mouseenterHandler, mouseleaveHandler }) => {
      element.removeEventListener("mouseenter", mouseenterHandler);
      element.removeEventListener("mouseleave", mouseleaveHandler);
    });
    this.hoverHandlers = [];
    
    // Clean up event listeners and resources
    if (this.mobileToggle) {
      this.mobileToggle.removeEventListener('click', this.openMobileMenu);
    }
    if (this.mobileClose) {
      this.mobileClose.removeEventListener('click', this.closeMobileMenu);
    }
    if (this.mobileOverlay) {
      this.mobileOverlay.removeEventListener('click', this.closeMobileMenu);
    }
    
    // Close mobile menu if open
    if (this.isMenuOpen) {
      this.closeMobileMenu();
    }
    
    // Clean up sticky instance if exists
    if ((this.menuBar as any).sticky) {
      (this.menuBar as any).sticky.destroy();
    }
    
    console.log('NAD Menu Bar destroyed');
  }


  public static initializeAll(): NADMenuBar[] {
    const menuBars = document.querySelectorAll<HTMLElement>('.nad-menu-bar');
    return Array.from(menuBars).map(() => new NADMenuBar());
  }

  public static destroySection(menuBar: HTMLElement): void {
    const instance = (menuBar as any).__nadMenuBar;
    if (instance && instance.destroy) {
      instance.destroy();
    }
  }
}

// Export for use in other modules
export { NADMenuBar };
